/**
 * 消耗明细里"这笔消耗属于哪个会话"的展示规则。
 *
 * 单独放在 lib 里而不是写在 `QuotaView` 的模板里，是因为这一段**全是判断**：
 * 契约只给了一个 `conversationId: string | null`（既没有标题，也没有"是否已删除"），
 * 而界面上必须把下面四种情况分开——其中任何一条判错，用户就会看到
 * "还在的会话被标成已删除"或者"已删除的会话还能点进去"。
 *
 * 四种情况：
 * 1. 命中会话列表 → 标题 + 链接（用户认的是标题，不是 cuid）
 * 2. 列表**完整**拉过但没命中 → 「会话已删除」，不发链接
 * 3. 列表没拉全（翻页上限 / 请求失败）→ 只显示短 id，且**不判定为已删除**
 * 4. `conversationId === null` → 「未关联会话」：null 的含义是"这次请求没带会话 id"，
 *    **不是**"会话被删了"（会话的第一轮请求就是这样，会话是服务端在流式过程中建的）
 */

export interface LedgerOwnerView {
  /** 展示文案 */
  label: string;
  /** 只有确认会话仍存在时才给；null 表示不可点 */
  href: string | null;
  /** 悬停说明 */
  hint: string | null;
}

/** 短 id：会话标题不可用时的兜底显示，只截前 8 位，避免整行被 cuid 挤满 */
export function shortConversationId(conversationId: string): string {
  return `会话 ${conversationId.slice(0, 8)}`;
}

export function describeLedgerOwner(
  conversationId: string | null,
  /** 会话 id → 标题，来自用户自己的会话列表 */
  titles: ReadonlyMap<string, string>,
  /** 会话列表是否**完整**拉取过：只有完整时才有资格断言"这个会话已经不存在" */
  indexComplete: boolean,
): LedgerOwnerView {
  if (conversationId === null) {
    return {
      label: '未关联会话',
      href: null,
      hint: '这条记录写入时没有带上会话（会话的第一轮提问就是这样），不代表会话被删除',
    };
  }

  const title = titles.get(conversationId);
  if (title !== undefined) {
    // 命中的判据是"id 在列表里"，标题是否为空不影响会话还存在这件事
    const trimmed = title.trim();
    return {
      label: trimmed === '' ? shortConversationId(conversationId) : trimmed,
      href: `/chat/${conversationId}`,
      hint: '回到这次对话',
    };
  }

  if (indexComplete) {
    return { label: '会话已删除', href: null, hint: '这条消耗所属的会话已被删除' };
  }

  return {
    label: shortConversationId(conversationId),
    href: null,
    hint: '会话列表没有完整加载，暂时无法确认这条记录属于哪个会话',
  };
}
